import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarIcon, Clock, DollarSign, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { createEnrollment, getCourseById, Enrollment } from "@/utils/firestoreService";
import { Course, CourseType } from "@/types/courseTypes";
import { cn } from "@/lib/utils";
import { useFirebase } from "@/contexts/FirebaseContext";
import { useAuth } from "@/contexts/AuthContext";
import { notifyNewEnrollment } from "@/utils/notificationService";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import styles from './PracticalCourseEnrollment.module.css';
import { Label } from "@/components/ui/label";
import { PaymentDialog } from "@/components/payment/PaymentDialog";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/config/firebase";

const timeSlots = [
  "09:00", "10:00", "11:00", "12:00", "13:00", 
  "14:00", "15:00", "16:00", "17:00"
];

const drivingExperienceOptions = [
  "None - Complete Beginner",
  "Some - Practiced with family/friends",
  "Intermediate - Had some lessons before",
  "Advanced - Preparing for test"
];

const vehicleTypeOptions = [
  "Manual Transmission",
  "Automatic Transmission",
  "No Preference"
];

const licenseTypeOptions = [
  "No License Yet",
  "Learner's Permit",
  "Provisional License",
  "Full License (Looking for Advanced Lessons)"
];

interface FormData {
  additionalNotes?: string;
  drivingExperience?: string;
  vehicleType?: string;
  licenseType?: string;
  previousLessons?: string;
  medicalConditions?: boolean;
  medicalDetails?: string;
  nervousAboutDriving?: boolean;
  specificGoals?: string;
  focusAreas?: string;
  paymentMethod?: 'cash' | 'card';
}

const PracticalCourseEnrollment = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const [loading, setLoading] = useState(false);
  const [course, setCourse] = useState<Course | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [selectedHours, setSelectedHours] = useState<number>(1);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [enrollmentId, setEnrollmentId] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentUser } = useFirebase();
  const { user } = useAuth();

  const form = useForm<FormData>({
    defaultValues: {
      drivingExperience: "",
      vehicleType: "",
      licenseType: "",
      previousLessons: "0",
      medicalConditions: false,
      medicalDetails: "",
      nervousAboutDriving: false,
      specificGoals: "",
      focusAreas: [],
    }
  });

  // Calculate price based on selected hours
  const calculatePrice = () => {
    if (!course) return 0;
    
    if (course.subtype === "individual") {
      // For individual courses, calculate based on rate per hour
      const amount = (course.pricePerHour || 0) * selectedHours;
      // Ensure minimum amount of £1.00 for Stripe
      return Math.max(amount, 1.00);
    }
    
    if (!isPartialPayment) {
      // Ensure minimum amount of £1.00 for Stripe
      return Math.max(course.price || 0, 1.00);
    }
    
    // For regular practical courses with partial payment
    const totalHours = course.totalHours || course.duration || 10;
    const pricePerHour = (course.price || 0) / totalHours;
    const partialAmount = pricePerHour * selectedHours;
    
    // Ensure minimum amount of £1.00 for Stripe
    return Math.max(Number(partialAmount.toFixed(2)), 1.00);
  };

  // Watch medical conditions value to show/hide conditional field
  const hasMedicalConditions = form.watch("medicalConditions");
  
  useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId) return;
      
      try {
        setLoading(true);
        const courseData = await getCourseById(courseId);
        console.log('Fetched course data:', courseData); // Debug log
        setCourse(courseData);
        // Only set total hours for non-individual courses
        if (courseData?.subtype !== "individual") {
          setSelectedHours(courseData?.totalHours || 0);
        }
      } catch (error) {
        console.error("Error fetching course:", error);
        toast({
          title: "Error",
          description: "Failed to load course details. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId, toast]);

  const handleEnroll = async (formData: FormData) => {
    if (!courseId || !course) return;
    if (!currentUser) {
      toast({
        title: "Authentication Required",
        description: "Please log in to book a course",
        variant: "destructive"
      });
      return;
    }

    setProcessingPayment(true);
    
    try {
      const studentName = user?.firstName && user?.lastName 
        ? `${user.firstName} ${user.lastName}`
        : currentUser.displayName || "Student";
      
      // Calculate the total amount based on price per hour and selected hours
      const totalAmount = course.subtype === "individual" 
        ? (course.pricePerHour || 0) * selectedHours 
        : course.price || 0;
      
      if (totalAmount <= 0) {
        throw new Error('Invalid amount calculated');
      }

      // Set total hours based on course type
      const totalHours = course.subtype === "individual" 
        ? selectedHours 
        : course.totalHours || course.duration || 10;
      
      // Create the enrollment data but don't save it yet
      const enrollmentData: Omit<Enrollment, 'id' | 'enrolledAt' | 'updatedAt'> = {
        userId: currentUser.uid,
        studentName: studentName,
        studentEmail: currentUser.email || "student@example.com",
        courseId: courseId,
        courseName: course.title,
        courseType: course.subtype === "individual" ? "individual" : "practical",
        status: "pending",
        enrollmentStatus: "pending",
        instructorId: "",
        instructorEmail: "",
        additionalNotes: formData.additionalNotes || "",
        drivingExperience: formData.drivingExperience || "",
        vehicleType: formData.vehicleType || "",
        licenseType: formData.licenseType || "",
        previousLessons: formData.previousLessons || "0",
        medicalConditions: formData.medicalConditions || false,
        medicalDetails: formData.medicalConditions ? formData.medicalDetails : "",
        nervousAboutDriving: formData.nervousAboutDriving || false,
        specificGoals: formData.specificGoals || "",
        focusAreas: formData.focusAreas || "",
        totalHours: totalHours,
        hoursTaken: 0,
        hoursRemaining: totalHours,
        amount: totalAmount,
        paymentStatus: "pending",
        isPartialPayment: false,
        amountPaid: 0,
        fullCoursePrice: totalAmount,
        remainingHours: totalHours,
        startDate: new Date().toISOString(),
        preferredTime: "To be scheduled",
        currency: "GBP"
      };

      // Store enrollment data temporarily
      const tempEnrollmentId = `temp_${Date.now()}`;
      setEnrollmentId(tempEnrollmentId);
      setShowPaymentDialog(true);
      
      // Store enrollment data in sessionStorage for later use
      sessionStorage.setItem('pendingEnrollment', JSON.stringify(enrollmentData));
      
    } catch (error) {
      console.error("Error preparing enrollment:", error);
      toast({
        title: "Enrollment Failed",
        description: "There was an error processing your enrollment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleBeforePayment = async (): Promise<void> => {
    try {
      const pendingEnrollment = sessionStorage.getItem('pendingEnrollment');
      if (!pendingEnrollment) {
        throw new Error('No pending enrollment found');
      }

      const enrollmentData = JSON.parse(pendingEnrollment);
      
      // Create the enrollment first
      const createdEnrollment = await createEnrollment(enrollmentData);
      
      if (!createdEnrollment || !createdEnrollment.id) {
        throw new Error('Failed to create enrollment');
      }

      // Update the enrollment data in sessionStorage with the real ID
      const updatedEnrollmentData = {
        ...enrollmentData,
        id: createdEnrollment.id,
        enrolledAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      sessionStorage.setItem('pendingEnrollment', JSON.stringify(updatedEnrollmentData));
      setEnrollmentId(createdEnrollment.id);
    } catch (error) {
      console.error("Error creating enrollment:", error);
      throw error;
    }
  };

  const handlePaymentComplete = async (status: 'pending' | 'paid', method: 'cash' | 'card') => {
    try {
      const pendingEnrollment = sessionStorage.getItem('pendingEnrollment');
      if (!pendingEnrollment) {
        throw new Error('No pending enrollment found');
      }

      const enrollmentData = JSON.parse(pendingEnrollment);
      
      if (!enrollmentData.id) {
        throw new Error('Invalid enrollment data');
      }

      // For card payments, update the enrollment status
      if (method === 'card' && status === 'paid') {
        const enrollmentRef = doc(db, "enrollments", enrollmentData.id);
        await updateDoc(enrollmentRef, {
          paymentStatus: 'paid',
          status: 'active',
          enrollmentStatus: 'active',
          updatedAt: new Date().toISOString()
        });
      }

      // Clear the pending enrollment from sessionStorage
      sessionStorage.removeItem('pendingEnrollment');
      
      toast({
        title: "Enrollment Submitted",
        description: method === 'cash' 
          ? "Your booking is pending payment. Please complete the payment at our office."
          : "Your booking is pending admin verification. You will be notified when an instructor is assigned.",
      });

      // Close the dialog and navigate
      setShowPaymentDialog(false);
      navigate("/student-dashboard/my-enrollments");
    } catch (error) {
      console.error("Error completing payment:", error);
      toast({
        title: "Error",
        description: "There was an error processing your payment. Please contact support.",
        variant: "destructive"
      });
      // Even on error, close the dialog and navigate away
      setShowPaymentDialog(false);
      navigate("/student-dashboard/my-enrollments");
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="student">
        <div className="flex justify-center items-center h-64">
          <p>Loading course details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout role="student">
        <div className="text-center py-12">
          <h2 className="text-xl font-bold mb-2">Course Not Found</h2>
          <p className="text-gray-500 mb-4">The course you're looking for could not be found.</p>
          <Button onClick={() => navigate("/student-dashboard/courses")}>
            Browse Courses
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="student">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-drivewise-900">Book Practical Course</h1>
            <p className="mt-4 text-lg text-drivewise-600">Schedule your practical driving course</p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            <Card className="border-2 border-drivewise-200 shadow-lg">
              <CardHeader className="bg-drivewise-50 rounded-t-lg">
                <CardTitle className="text-2xl font-bold text-drivewise-900">{course?.title}</CardTitle>
                <CardDescription className="text-lg text-drivewise-600">{course?.description}</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-drivewise-100">
                    <Clock className="h-6 w-6 text-drivewise-600" />
                    <div>
                      <p className="font-medium text-drivewise-900">
                        {course.subtype === "individual" 
                          ? "Flexible Hours"
                          : `${course.duration} hours`}
                      </p>
                      <p className="text-sm text-drivewise-600">
                        {course.subtype === "individual"
                          ? "Choose your preferred number of hours"
                          : "Total course duration"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-drivewise-100">
                    <div>
                      <p className="font-medium text-drivewise-900">
                        {course.subtype === "individual"
                          ? `£${course.pricePerHour?.toFixed(2)}/hour`
                          : `£${course.price?.toFixed(2)}`}
                      </p>
                      <p className="text-sm text-drivewise-600">
                        {course.subtype === "individual"
                          ? "Rate per hour"
                          : "Total course price"}
                      </p>
                    </div>
                  </div>

                  {course.subtype === "individual" && (
                    <div className="p-4 bg-white rounded-lg border border-drivewise-100">
                      <Label htmlFor="hours" className="text-drivewise-900">Select Number of Hours:</Label>
                      <Select
                        value={selectedHours.toString()}
                        onValueChange={(value) => setSelectedHours(parseInt(value))}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select hours" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 20 }, (_, i) => i + 1).map((hours) => (
                            <SelectItem key={hours} value={hours.toString()}>
                              {hours} {hours === 1 ? "hour" : "hours"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="mt-2 text-right">
                        <p className="text-sm text-drivewise-600">Total: £{(course.pricePerHour * selectedHours).toFixed(2)}</p>
                      </div>
                    </div>
                  )}

                  {course.subtype !== "individual" && (
                    <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-drivewise-100">
                      <Switch
                        id="partial-payment"
                        checked={isPartialPayment}
                        onCheckedChange={setIsPartialPayment}
                      />
                      <Label htmlFor="partial-payment" className="text-drivewise-900">
                        Pay in installments
                      </Label>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-drivewise-200 shadow-lg">
              <CardHeader className="bg-drivewise-50 rounded-t-lg">
                <CardTitle className="text-2xl font-bold text-drivewise-900">Booking Information</CardTitle>
                <CardDescription className="text-lg text-drivewise-600">
                  To serve you better, please provide as much detail as possible
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleEnroll)} className="space-y-6">
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="experience" className="border rounded-lg mt-4">
                        <AccordionTrigger className="px-4 py-3 text-lg font-semibold hover:bg-drivewise-50">
                          Driving Experience
                        </AccordionTrigger>
                        <AccordionContent className="px-4 py-4 space-y-4">
                          <FormField
                            control={form.control}
                            name="drivingExperience"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-base font-medium">Current Experience Level</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <FormControl>
                                    <SelectTrigger className="w-full">
                                      <SelectValue placeholder="Select your experience level" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {drivingExperienceOptions.map((option) => (
                                      <SelectItem key={option} value={option}>
                                        {option}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="licenseType"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-base font-medium">License Status</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <FormControl>
                                    <SelectTrigger className="w-full">
                                      <SelectValue placeholder="Select your license status" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {licenseTypeOptions.map((option) => (
                                      <SelectItem key={option} value={option}>
                                        {option}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="previousLessons"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-base font-medium">Number of Previous Driving Lessons</FormLabel>
                                <FormControl>
                                  <Input 
                                    type="number"
                                    placeholder="0"
                                    min="0"
                                    className="w-full"
                                    {...field}
                                  />
                                </FormControl>
                                <FormDescription>
                                  Enter 0 if you've never had formal lessons
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </AccordionContent>
                      </AccordionItem>

                      <AccordionItem value="preferences" className="border rounded-lg mt-4">
                        <AccordionTrigger className="px-4 py-3 text-lg font-semibold hover:bg-drivewise-50">
                          Preferences & Requirements
                        </AccordionTrigger>
                        <AccordionContent className="px-4 py-4 space-y-4">
                          <FormField
                            control={form.control}
                            name="vehicleType"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-base font-medium">Vehicle Preference</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <FormControl>
                                    <SelectTrigger className="w-full">
                                      <SelectValue placeholder="Select vehicle type" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {vehicleTypeOptions.map((option) => (
                                      <SelectItem key={option} value={option}>
                                        {option}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="nervousAboutDriving"
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 bg-white">
                                <div className="space-y-0.5">
                                  <FormLabel className="text-base font-medium">
                                    Nervous About Driving
                                  </FormLabel>
                                  <FormDescription>
                                    Let us know if you feel anxious about driving so we can tailor our approach
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="specificGoals"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-base font-medium">Your Specific Goals</FormLabel>
                                <FormControl>
                                  <Textarea
                                    placeholder="E.g., Pass driving test, gain confidence, learn parking skills"
                                    className="resize-none min-h-[100px]"
                                    {...field}
                                  />
                                </FormControl>
                                <FormDescription>
                                  What do you hope to achieve with these lessons?
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={form.control}
                            name="focusAreas"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-base font-medium">Areas You Want to Focus On</FormLabel>
                                <FormControl>
                                  <Textarea
                                    placeholder="E.g., Parallel parking, roundabouts, lane changes, highway driving"
                                    className="resize-none min-h-[100px]"
                                    {...field}
                                  />
                                </FormControl>
                                <FormDescription>
                                  Tell us what driving skills you want to improve
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </AccordionContent>
                      </AccordionItem>

                      <AccordionItem value="health" className="border rounded-lg mt-4">
                        <AccordionTrigger className="px-4 py-3 text-lg font-semibold hover:bg-drivewise-50">
                          Health & Safety
                        </AccordionTrigger>
                        <AccordionContent className="px-4 py-4 space-y-4">
                          <FormField
                            control={form.control}
                            name="medicalConditions"
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 bg-white">
                                <div className="space-y-0.5">
                                  <FormLabel className="text-base font-medium">
                                    Medical Conditions
                                  </FormLabel>
                                  <FormDescription>
                                    Do you have any medical conditions that might affect your driving?
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          {hasMedicalConditions && (
                            <FormField
                              control={form.control}
                              name="medicalDetails"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-base font-medium">
                                    <span className="flex items-center">
                                      <AlertCircle className="h-5 w-5 text-amber-500 mr-2" />
                                      Medical Details
                                    </span>
                                  </FormLabel>
                                  <FormControl>
                                    <Textarea
                                      placeholder="Please provide details about any medical conditions that might affect your driving"
                                      className="resize-none min-h-[100px]"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormDescription>
                                    This information will be kept confidential and only used to ensure your lessons are safe and appropriate
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                    
                    <Separator className="my-6" />
                    
                    <div className="space-y-4">
                      <Button 
                        type="submit" 
                        className="w-full py-6 text-lg font-semibold bg-drivewise-600 hover:bg-drivewise-700"
                        disabled={processingPayment}
                      >
                        {processingPayment ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Processing...
                          </div>
                        ) : (
                          "Complete Booking"
                        )}
                      </Button>

                      {showPaymentDialog && (
                        <PaymentDialog
                          isOpen={showPaymentDialog}
                          onClose={() => setShowPaymentDialog(false)}
                          enrollmentId={enrollmentId}
                          amount={course?.subtype === "individual" 
                            ? (course.pricePerHour || 0) * selectedHours 
                            : course.price || 0}
                          onPaymentComplete={handlePaymentComplete}
                          onBeforePayment={handleBeforePayment}
                        />
                      )}
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PracticalCourseEnrollment;
